/**
 * DocumentSearch — Full-text search bar for the SBA document viewer.
 *
 * Renders a search input + button that calls the document search API,
 * then displays results with page navigation links.
 */
import React, { useState, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { searchDocument as sbaSearchDocument } from '../../api/viewerApi';
import type { SearchResult, SearchResponse } from '../../types/viewer';

interface DocumentSearchProps {
  docId: number;
  /** Called when user clicks a search result — navigates to that page. The
   *  second arg is the query that produced this result set, so a bilingual
   *  viewer can auto-select + translate the matching block on the landing
   *  page (monolingual viewers ignore it). */
  onNavigate: (pageNum: number, query?: string) => void;
  /** Optional override — pass the EPAR variant when this viewer is
   *  rendering an EPAR document instead of an SBA.  Defaults to the
   *  SBA viewerApi.searchDocument so existing call sites stay
   *  byte-for-byte identical. */
  searchFn?: (docId: number, query: string, limit?: number) => Promise<SearchResponse>;
  /** Notifies the parent of the active search phrase (or null when cleared)
   *  so the viewer can box the matched word on the page like Ctrl+F. */
  onActiveQueryChange?: (query: string | null) => void;
  /** Optional ref to the search <input>, so the viewer can focus this box
   *  when the user presses ⌘F/Ctrl+F (the in-document search replaces native
   *  browser Find on large/virtualized docs whose full text isn't in the DOM). */
  inputRef?: React.Ref<HTMLInputElement>;
}

const DocumentSearch: React.FC<DocumentSearchProps> = ({
  docId,
  onNavigate,
  searchFn = sbaSearchDocument,
  onActiveQueryChange,
  inputRef,
}) => {
  const [query, setQuery] = useState('');
  // The query that produced ``results``. Kept separate from ``query`` (the live
  // input, which the user may edit without re-searching) so a result click
  // reports the term the results actually correspond to.
  const [resultsQuery, setResultsQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setIsSearching(true);
    setError(null);
    setHasSearched(true);
    try {
      const resp = await searchFn(docId, trimmed);
      // Present matches in reading order (ascending page number) rather than
      // the backend's relevance-rank order — a "find in document" list should
      // read top-to-bottom (p.14, 17, 21 …), not p.37, 40, 14. The jp_pdf
      // server search returns rank DESC; the client search is already
      // page-ordered, so this sort is a harmless no-op there. Stable + copies
      // the array (never mutate the response).
      const ordered = [...resp.results].sort((a, b) => a.page_num - b.page_num);
      setResults(ordered);
      setResultsQuery(trimmed);
      // Box the phrase on the page (Ctrl+F-style), not just in this list.
      onActiveQueryChange?.(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [docId, query, searchFn, onActiveQueryChange]);

  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setResultsQuery('');
    setError(null);
    setHasSearched(false);
    onActiveQueryChange?.(null);
  }, [onActiveQueryChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  return (
    <div className="px-2 py-2 border-b border-gray-200">
      {/* Search input */}
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search in document..."
            className="w-full pl-7 pr-7 py-1.5 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
          />
          {query && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={isSearching || !query.trim()}
          className="px-2 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Search"
        >
          {isSearching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}

      {/* Search results */}
      {hasSearched && !isSearching && !error && (
        <div className="mt-2">
          {results.length === 0 ? (
            <p className="text-xs text-gray-500 px-1">No results found.</p>
          ) : (
            <div className="max-h-60 overflow-y-auto space-y-1">
              <p className="text-xs text-gray-500 px-1 mb-1">
                {results.length} result{results.length !== 1 ? 's' : ''}
              </p>
              {results.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => onNavigate(result.page_num, resultsQuery)}
                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-indigo-600 whitespace-nowrap">
                      p. {result.page_num}
                    </span>
                    <span
                      className="text-gray-700 text-xs line-clamp-2 [&_mark]:bg-yellow-200 [&_mark]:rounded-sm"
                      dangerouslySetInnerHTML={{ __html: result.headline }}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentSearch;
